import React, { useEffect, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { Banknote, Bot, FileText } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import EvidenceActions from './EvidenceActions';
import ReconciliationHistory from './ReconciliationHistory';
import ReconciliationWorkStatus, { useReconciliationWork } from './ReconciliationWorkStatus';
import { executeAction, getAction, getCapabilities, prepareAction, resolveOwnRecord } from '../../../../services/ReconciliationActionService';
import { supportsBankReconciliation } from '../../../../services/MatchingService.contract';
import { buildConfirmTargets, getBankStatement } from '../../../../services/MatchingService';
import InvoicePickerModal from './InvoicePickerModal';

const SIDE_BY_TARGET = { customer_invoice: 'customer', supplier_invoice: 'supplier' };

/**
 * Owns the reconciliation state for one bank line and hands the sheet three
 * small slots through the `reconciliation` prop on its child:
 *   agent        — one line about what Mats is doing (null when no case)
 *   manualPaths  — text actions for the ⋮ menu
 *   record       — what was already done, with undo (null when nothing)
 * The child stays the sheet's only layout element; the modals are portals.
 *
 * Confirming a suggestion is one request to the backend, which asks the
 * service that owns the record to do the work (the invoice service registers
 * the payment, exactly as "Registrera betalning" on the invoice does). The
 * engine already decided WHAT matches; this never searches again, and the
 * user is never asked to pick what they already picked.
 *
 *   The click carries the action out, inline: no modal. The footer button
 *   shows that it is working, a refusal is a line in the sheet with the
 *   backend's reason, and the sheet moves to the next row when it is done.
 *     verifikat target    → link_existing_verification by the verifikat id
 *     payroll target      → the backend names the payroll run's own verifikat
 *     Att bekräfta        → the backend names what the engine pre-linked
 *     registered payment  → link_existing_payment
 *     payment file        → settle_payment_rows with the rows the engine named
 *     unpaid invoice      → register_customer_payment / register_supplier_payment
 *   A registered payment is announced with a toast that leads to the invoice;
 *   a link books nothing and is not announced.
 *
 *   One case needs the user first: a supplier invoice that is booked when it
 *   is paid (kontantmetoden). There the click opens the kontering step in the
 *   sheet (`onBookPayment` on the child), as the invoice page does.
 *
 * The modal is only the manual way to find an invoice (⋮ menu). Choosing
 * there leads to the same two paths.
 */
export default function ReconciliationWorkspace({ companyId, line, onSettled, children }) {
    const [enabled,setEnabled] = useState(false), [pickerOpen,setPickerOpen] = useState(false);
    const [evidenceEnabled,setEvidenceEnabled] = useState(false), [evidenceOpen,setEvidenceOpen] = useState(false);
    const [linking,setLinking] = useState(false), [linkError,setLinkError] = useState('');
    const history=useHistory();
    const work=useReconciliationWork(companyId,line,enabled);
    useEffect(() => {let active=true;getCapabilities(companyId).then(c => {if(active){setEnabled(supportsBankReconciliation(c));setEvidenceEnabled(c.actions?.includes('attach_evidence') && c.actions?.includes('link_existing_verification'));}}).catch(() => {if(active)setEnabled(false);});return () => {active=false;};},[companyId]);

    // An action needs the bank line and the record it points at, nothing more.
    // `resolve` names that record. The line is re-read only for its current
    // revision, so a stale list never produces a stale plan. Prepare and
    // execute run back to back: the backend validates the action either way.
    const run = async (resolve) => {
        if(linking) return;
        setLinking(true); setLinkError('');
        let plan=null;
        try {
            const [own, fresh] = await Promise.all([resolve(), getBankStatement(companyId, line.id)]);
            plan = await prepareAction(companyId, {
                contract: 'bank-reconciliation/1',
                request_id: uuid(),
                statement_id: line.id,
                type: own.type,
                expected_bank_revision: Number(fresh.reconciliation_revision),
                expected_bank_generation: Number(fresh.reconciliation_generation),
                target: own.target,
                evidence_ids: [],
            });
            let result;
            try { result = await executeAction(companyId, plan.action_id, { plan_hash: plan.plan_hash, request_id: uuid() }); }
            catch (e) {
                // A lost response says nothing about the outcome: ask what became of the action.
                if(e.response && e.response.status < 500) throw e;
                result = (await getAction(companyId, plan.action_id)).result;
                if(!result) throw e;
            }
            if(result.created_payment) {
                const supplier = plan.type === 'register_supplier_payment';
                toast.success('Betalningen är registrerad och bokförd.', {
                    action: { label:'Visa faktura', onClick:() => history?.push(`/${companyId}/${supplier ? 'supplier-invoices' : 'view-invoice'}/${plan.target.invoice_id}`) },
                });
            }
            onSettled?.();
        } catch (e) {
            // The backend says why (wrong amount, already linked, period …).
            setLinkError(e.response?.data?.message || (plan
                ? 'Resultatet kunde inte bekräftas. Kontrollera bankhändelsen innan du försöker igen.'
                : 'Åtgärden kunde inte genomföras. Försök igen.'));
            // Show the truth when the outcome is unknown.
            if(!e.response) onSettled?.();
        } finally { setLinking(false); }
    };

    // The payment of an invoice: `target` is { side, id, paymentId?, needsKontering?, number?, counterparty? }.
    const settleInvoice = (target) => {
        if(target.paymentId) {
            return run(async () => ({ type:'link_existing_payment', target:{ side:target.side, invoice_id:target.id, payment_id:target.paymentId } }));
        }
        if(target.side === 'supplier' && target.needsKontering) {
            children.props.onBookPayment?.({ side:'supplier', invoiceId:target.id, invoiceNumber:target.number, counterparty:target.counterparty });
            return undefined;
        }
        return run(async () => ({ type:`register_${target.side}_payment`, target:{ invoice_id:target.id } }));
    };

    const confirm = (suggestion,options) => {
        // A payment file the company sent (plan §3.11.3): the rows named by the
        // suggestion are settled by one action; the backend re-derives every row.
        if(enabled && suggestion?.paymentFile && Array.isArray(suggestion.instructionIds) && suggestion.instructionIds.length) {
            return run(async () => ({ type:'settle_payment_rows', target:{ instruction_ids: suggestion.instructionIds, provenance: suggestion.evidence || 'manual' } }));
        }
        const targets = buildConfirmTargets(suggestion);
        const invoiceTarget = targets.find(t => SIDE_BY_TARGET[t.target_type]);
        if(enabled && invoiceTarget) {
            const side = SIDE_BY_TARGET[invoiceTarget.target_type];
            return settleInvoice({
                side, id:invoiceTarget.target_id,
                paymentId: suggestion.paymentId || suggestion.payment_id || null,
                needsKontering: !!suggestion.needsKontering,
                number: Array.isArray(suggestion.invoiceNumbers) ? suggestion.invoiceNumbers.join(' + ') : suggestion.invoiceNumber,
                counterparty: side === 'supplier' ? suggestion.supplierName : suggestion.customerName,
            });
        }
        const verificationTarget = targets.find(t => t.target_type === 'verification');
        if(enabled && verificationTarget) {
            return run(async () => ({ type:'link_existing_verification', target:{ verification_id: verificationTarget.target_id } }));
        }
        const payrollTarget = targets.find(t => t.target_type === 'payroll_run');
        if(enabled && payrollTarget) {
            return run(() => resolveOwnRecord(companyId, line.id, { target_type:'payroll_run', target_id: payrollTarget.target_id }));
        }
        // Deprecated fallback: the old confirm endpoint, reached only when the
        // backend has no reconciliation contract. Remove with the old bank writers.
        return children.props.onConfirmSuggestion?.(suggestion,options);
    };
    // Att bekräfta: the engine pre-linked the row at import. Bekräfta accepts
    // exactly that link. The `onConfirmSingle` branch is the same deprecated
    // fallback as above.
    const confirmSingle = () => (enabled
        ? run(() => resolveOwnRecord(companyId, line.id))
        : children.props.onConfirmSingle?.());
    // Chosen by hand in the picker: from here on it is the same as a suggestion.
    const pick = (invoice,payment=null) => {
        setPickerOpen(false);
        return settleInvoice({
            side:invoice.side, id:invoice.id, paymentId:payment?.payment_id || null,
            needsKontering: invoice.side === 'supplier' && invoice.accounting_method === 'cash',
            number:invoice.number, counterparty:invoice.counterparty_name,
        });
    };
    const settled = line.reconciliation_status === 'verified';
    const reconciliation = enabled ? {
        linking,
        notice: linkError ? <Alert variant="warning" className="py-2" style={{ fontSize: '0.8125rem' }} dismissible onClose={() => setLinkError('')}>{linkError}</Alert> : null,
        agent: <ReconciliationWorkStatus companyId={companyId} line={line} state={work}/>,
        manualPaths: [
            { key:'invoice', icon:Banknote, label:'Koppla till faktura eller betalning', onClick:() => setPickerOpen(true), disabled:settled },
            ...(evidenceEnabled ? [{ key:'verification', icon:FileText, label:'Koppla till befintligt verifikat', onClick:() => setEvidenceOpen(true), disabled:settled }] : []),
            ...(!work.data?.work && !work.unavailable ? [{ key:'agent', icon:Bot, label:'Låt Mats undersöka', onClick:work.start, disabled:work.busy }] : []),
        ],
        record: <ReconciliationHistory companyId={companyId} statementId={line.id} onChanged={onSettled}/>,
    } : undefined;
    return <>
        {React.cloneElement(children,{onConfirmSuggestion:confirm,onConfirmSingle:confirmSingle,reconciliation,
            // The footer's primary button shows its spinner while an action runs.
            actionBusy: children.props.actionBusy || linking})}
        {enabled && evidenceEnabled && <EvidenceActions companyId={companyId} line={line} show={evidenceOpen} onHide={() => setEvidenceOpen(false)} onChanged={onSettled}/>}
        <Modal show={pickerOpen} onHide={() => setPickerOpen(false)} centered>
            <Modal.Header closeButton><Modal.Title as="h6">Koppla till faktura eller betalning</Modal.Title></Modal.Header>
            <Modal.Body>
                {pickerOpen && <InvoicePickerModal companyId={companyId} line={line} busy={linking} onPick={pick} />}
            </Modal.Body>
        </Modal>
    </>;
}
